import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getApiErrorMessage, jagranNextService, unwrapList } from "../../apiService/jagranNext.service";
import {
  DangerButton,
  EmptyState,
  Field,
  formatWhen,
  inputClass,
  Notice,
  PageShell,
  Panel,
  PrimaryButton,
  SecondaryButton,
  StatusBadge,
  toIso,
} from "./ui";

const emptySeason = { name: "", price: "", start_date: "", end_date: "", status: "upcoming" };

export default function CampaignDetail() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [form, setForm] = useState(emptySeason);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [campaignResponse, seasonResponse] = await Promise.all([
        jagranNextService.getCampaign(campaignId),
        jagranNextService.listSeasons(campaignId, { limit: 100, offset: 0 }),
      ]);
      setCampaign(campaignResponse.data || null);
      setSeasons(unwrapList(seasonResponse.data).rows);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Reload only when the campaign route changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  const updateField = (key) => (event) => {
    setForm((current) => ({ ...current, [key]: event.target.value }));
  };

  const createSeason = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      await jagranNextService.createSeason(campaignId, {
        name: form.name.trim(),
        price: Number(form.price),
        start_date: toIso(form.start_date),
        end_date: toIso(form.end_date),
        status: form.status,
      });
      setForm(emptySeason);
      await load();
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setSaving(false);
    }
  };

  const removeSeason = async (season) => {
    if (!window.confirm(`Delete ${season.name}?`)) return;
    setError("");
    try {
      await jagranNextService.deleteSeason(campaignId, season.id);
      await load();
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    }
  };

  return (
    <PageShell
      title={campaign?.name || "Campaign"}
      description={campaign?.description || "Seasons, pricing, and dates for this campaign."}
      actions={
        <>
          <SecondaryButton onClick={() => navigate("/products/jagran-next")}>Campaigns</SecondaryButton>
          <SecondaryButton
            onClick={() => navigate(`/products/jagran-next/leads?campaign_id=${campaignId}`)}
          >
            Leads
          </SecondaryButton>
          <PrimaryButton
            onClick={() => navigate(`/products/jagran-next/campaigns/${campaignId}/edit`)}
            disabled={campaign?.status === "archived"}
          >
            Edit
          </PrimaryButton>
        </>
      }
    >
      <Notice>{error}</Notice>
      {loading ? <p className="text-sm text-gray-500">Loading campaign...</p> : null}
      {campaign ? (
        <div className="mb-4 flex flex-wrap items-center gap-3 text-sm text-gray-600">
          <span className="rounded-full bg-white px-3 py-1 ring-1 ring-gray-200">Slug {campaign.slug || "-"}</span>
          <StatusBadge value={campaign.status} />
        </div>
      ) : null}

      <Panel title="Add a season" description="Price is in rupees. Ended seasons cannot be checked out.">
        <form className="grid gap-4 md:grid-cols-2" onSubmit={createSeason}>
          <Field label="Name" required>
            <input className={inputClass} placeholder="Season 1" value={form.name} onChange={updateField("name")} required />
          </Field>
          <Field label="Price" required>
            <input className={inputClass} type="number" min="0" step="0.01" placeholder="199" value={form.price} onChange={updateField("price")} required />
          </Field>
          <Field label="Starts" required>
            <input className={inputClass} type="datetime-local" value={form.start_date} onChange={updateField("start_date")} required />
          </Field>
          <Field label="Ends" required>
            <input className={inputClass} type="datetime-local" value={form.end_date} onChange={updateField("end_date")} required />
          </Field>
          <Field label="Status">
            <select className={inputClass} value={form.status} onChange={updateField("status")}>
              <option value="upcoming">Upcoming</option>
              <option value="active">Active</option>
              <option value="ended">Ended</option>
            </select>
          </Field>
          <div className="flex items-end">
            <PrimaryButton type="submit" disabled={saving || campaign?.status === "archived"}>
              {saving ? "Saving..." : "Add season"}
            </PrimaryButton>
          </div>
        </form>
      </Panel>

      <div className="mt-4">
      {seasons.length === 0 && !loading ? (
        <EmptyState title="No seasons yet" description="Add the first season to set pricing and open registration." />
      ) : null}
      {seasons.length > 0 ? (
        <Panel>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-3 py-3">Season</th>
                  <th className="px-3 py-3">Price</th>
                  <th className="px-3 py-3">Starts</th>
                  <th className="px-3 py-3">Ends</th>
                  <th className="px-3 py-3">Status</th>
                  <th className="px-3 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {seasons.map((season) => (
                  <tr key={season.id}>
                    <td className="px-3 py-3 font-semibold text-gray-900">
                      {season.season_number ? `${season.season_number}. ` : ""}
                      {season.name}
                    </td>
                    <td className="px-3 py-3">₹{season.price}</td>
                    <td className="px-3 py-3 text-gray-600">{formatWhen(season.start_date)}</td>
                    <td className="px-3 py-3 text-gray-600">{formatWhen(season.end_date)}</td>
                    <td className="px-3 py-3"><StatusBadge value={season.status} /></td>
                    <td className="px-3 py-3">
                      <div className="flex gap-2">
                        <SecondaryButton
                          onClick={() => navigate(`/products/jagran-next/campaigns/${campaignId}/seasons/${season.id}`)}
                        >
                          Manage
                        </SecondaryButton>
                        <DangerButton onClick={() => removeSeason(season)}>Delete</DangerButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      ) : null}
      </div>
    </PageShell>
  );
}
